import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Activity,
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock3,
  FolderKanban,
  Mail,
  MessageSquare,
  Newspaper,
  Save,
  Shield,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import UserContactLinks from '../components/UserContactLinks';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useApp } from '../providers/AppProvider';
import { updateUser } from '../services/dataService';
import type { ChangeLog, Comment, NewsFeedComment, Task, User } from '../types';
import { parseDateLike } from '../utils/dateUtils';
import {
  getProfileCompletionWidthClass,
  getUserIdentitySet,
  getUserProfileCompletion,
  normalizeUserIdentity,
  usersMatch,
} from '../utils/userProfile';
import { getStatusColors } from '../constants/colorScheme';

type EditableProfileFields = {
  phoneNumber: string;
  linkedinUrl: string;
  githubUrl: string;
  facebookUrl: string;
};

function ProfileStatCard({
  label,
  value,
  helper,
  icon: Icon,
}: {
  label: string;
  value: string;
  helper?: string;
  icon: typeof Activity;
}) {
  return (
    <Card className="gap-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold text-foreground">{value}</div>
        {helper ? <p className="mt-1 text-xs text-muted-foreground">{helper}</p> : null}
      </CardContent>
    </Card>
  );
}

const toTime = (value: unknown): number => {
  const parsed = parseDateLike(value);
  return parsed ? parsed.getTime() : 0;
};

const formatTimestamp = (value: unknown): string => {
  const parsed = parseDateLike(value);
  return parsed ? parsed.toLocaleDateString() : 'No activity yet';
};

const getInitials = (name?: string, email?: string): string => {
  const source = String(name || email || 'User').trim();
  const parts = source.split(' ').filter(Boolean);

  if (parts.length > 1) {
    return parts
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  return source.slice(0, 2).toUpperCase();
};

export default function UserProfile() {
  const { userId } = useParams<{ userId?: string }>();
  const { state, loading, reload } = useApp();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EditableProfileFields>({
    phoneNumber: '',
    linkedinUrl: '',
    githubUrl: '',
    facebookUrl: '',
  });

  const viewer = state?.currentUser ?? null;

  const profileUser = useMemo<User | null>(() => {
    if (!state?.currentUser) {
      return null;
    }

    if (!userId) {
      return state.currentUser;
    }

    const targetIdentity = normalizeUserIdentity(decodeURIComponent(userId));
    const candidates = [state.currentUser, ...(state.users ?? [])].filter(Boolean) as User[];

    return (
      candidates.find((user) => getUserIdentitySet(user).has(targetIdentity)) || null
    );
  }, [state?.currentUser, state?.users, userId]);

  const canEdit = usersMatch(viewer, profileUser);

  useEffect(() => {
    setForm({
      phoneNumber: profileUser?.phoneNumber || '',
      linkedinUrl: profileUser?.linkedinUrl || '',
      githubUrl: profileUser?.githubUrl || '',
      facebookUrl: profileUser?.facebookUrl || '',
    });
  }, [profileUser?.facebookUrl, profileUser?.githubUrl, profileUser?.linkedinUrl, profileUser?.phoneNumber]);

  const organizationName = useMemo(() => {
    if (!profileUser?.organizationId) {
      return 'No organization assigned';
    }

    return (
      state?.organizations.find((organization) => organization.id === profileUser.organizationId)?.name ||
      profileUser.organizationId
    );
  }, [profileUser?.organizationId, state?.organizations]);

  const assignedTasks = useMemo(() => {
    if (!profileUser) {
      return [] as Task[];
    }

    const identities = getUserIdentitySet(profileUser);
    return (state?.tasks ?? []).filter((task) =>
      (task.assigneeIds ?? []).some((assigneeId) => identities.has(normalizeUserIdentity(assigneeId)))
    );
  }, [profileUser, state?.tasks]);

  const relatedProjects = useMemo(() => {
    if (!profileUser) {
      return [] as Array<{ id: string; name: string }>;
    }

    const identities = getUserIdentitySet(profileUser);
    const projectsById = new Map<string, { id: string; name: string }>();

    (state?.projects ?? []).forEach((project) => {
      const isTeamMember = (project.teamMembers ?? []).some((member) =>
        identities.has(normalizeUserIdentity(member.userId))
      );
      const isLead = (project.projectLeads ?? []).some((leadId) =>
        identities.has(normalizeUserIdentity(leadId))
      );
      const isExplicitlyAllowed = (project.allowedUserIds ?? []).some((allowedUserId) =>
        identities.has(normalizeUserIdentity(allowedUserId))
      );

      if (isTeamMember || isLead || isExplicitlyAllowed) {
        projectsById.set(project.id, { id: project.id, name: project.name });
      }
    });

    assignedTasks.forEach((task) => {
      const project = (state?.projects ?? []).find((candidate) => candidate.id === task.projectId);
      if (project) {
        projectsById.set(project.id, { id: project.id, name: project.name });
      }
    });

    return Array.from(projectsById.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [assignedTasks, profileUser, state?.projects]);

  const completedTasks = useMemo(
    () => assignedTasks.filter((task) => task.status === 'Completed'),
    [assignedTasks],
  );

  const activeTasks = useMemo(
    () =>
      assignedTasks.filter(
        (task) => !['Completed', 'Deferred', 'Not Required'].includes(task.status),
      ),
    [assignedTasks],
  );

  const totalLoggedHours = useMemo(() => {
    if (!profileUser) {
      return 0;
    }

    const identities = getUserIdentitySet(profileUser);
    return assignedTasks.reduce((total, task) => {
      const taskHours = (task.timeLogs ?? [])
        .filter((timeLog) => identities.has(normalizeUserIdentity(timeLog.userId)))
        .reduce((sum, timeLog) => sum + (Number(timeLog.hours) || 0), 0);

      return total + taskHours;
    }, 0);
  }, [assignedTasks, profileUser]);

  const averageTaskRating = useMemo(() => {
    const ratedTasks = assignedTasks.filter((task) => typeof task.rating === 'number');
    if (!ratedTasks.length) {
      return null;
    }

    const total = ratedTasks.reduce((sum, task) => sum + Number(task.rating || 0), 0);
    return total / ratedTasks.length;
  }, [assignedTasks]);

  const standupCount = useMemo(() => {
    if (!profileUser) {
      return 0;
    }

    const identities = getUserIdentitySet(profileUser);
    return (state?.standupNotes ?? []).filter((note) =>
      identities.has(normalizeUserIdentity(note.authorId)),
    ).length;
  }, [profileUser, state?.standupNotes]);

  const feedPostsCount = useMemo(() => {
    if (!profileUser) {
      return 0;
    }

    const identities = getUserIdentitySet(profileUser);
    return (state?.newsFeedPosts ?? []).filter((post) =>
      identities.has(normalizeUserIdentity(post.authorId)),
    ).length;
  }, [profileUser, state?.newsFeedPosts]);

  const commentCount = useMemo(() => {
    if (!profileUser) {
      return 0;
    }

    const identities = getUserIdentitySet(profileUser);
    const countComments = (comments?: Comment[]) =>
      (comments ?? []).filter((comment) => identities.has(normalizeUserIdentity(comment.createdBy))).length;
    const countFeedComments = (comments?: NewsFeedComment[]) =>
      (comments ?? []).filter((comment) => identities.has(normalizeUserIdentity(comment.authorId))).length;

    return (
      (state?.tasks ?? []).reduce((sum, task) => sum + countComments(task.comments), 0) +
      (state?.projects ?? []).reduce((sum, project) => sum + countComments(project.comments), 0) +
      (state?.epics ?? []).reduce((sum, epic) => sum + countComments(epic.comments), 0) +
      (state?.stories ?? []).reduce((sum, story) => sum + countComments(story.comments), 0) +
      (state?.sprints ?? []).reduce((sum, sprint) => sum + countComments(sprint.comments), 0) +
      (state?.retrospectives ?? []).reduce((sum, retrospective) => sum + countComments(retrospective.comments), 0) +
      (state?.reports ?? []).reduce((sum, report) => sum + countComments(report.comments), 0) +
      (state?.newsFeedPosts ?? []).reduce((sum, post) => sum + countFeedComments(post.postComments), 0)
    );
  }, [profileUser, state?.epics, state?.newsFeedPosts, state?.projects, state?.reports, state?.retrospectives, state?.sprints, state?.stories, state?.tasks]);

  const recentChanges = useMemo(() => {
    if (!profileUser) {
      return [] as Array<ChangeLog & { taskTitle: string }>;
    }

    const identities = getUserIdentitySet(profileUser);
    return (state?.tasks ?? [])
      .flatMap((task) =>
        (task.changeLogs ?? [])
          .filter((change) => identities.has(normalizeUserIdentity(change.changedBy)))
          .map((change) => ({ ...change, taskTitle: task.title })),
      )
      .sort((left, right) => toTime(right.changedAt) - toTime(left.changedAt));
  }, [profileUser, state?.tasks]);

  const recentTasks = useMemo(
    () => [...assignedTasks].sort((left, right) => toTime(right.updatedAt) - toTime(left.updatedAt)).slice(0, 6),
    [assignedTasks],
  );

  const lastActiveAt = useMemo(() => {
    if (!profileUser) {
      return 0;
    }

    const identities = getUserIdentitySet(profileUser);
    const activityCandidates = [
      ...assignedTasks.flatMap((task) => [
        task.updatedAt,
        ...(task.timeLogs ?? [])
          .filter((timeLog) => identities.has(normalizeUserIdentity(timeLog.userId)))
          .map((timeLog) => timeLog.date),
      ]),
      ...(state?.standupNotes ?? [])
        .filter((note) => identities.has(normalizeUserIdentity(note.authorId)))
        .map((note) => note.createdAt),
      ...recentChanges.map((change) => change.changedAt),
    ];

    return activityCandidates
      .map((value) => toTime(value))
      .reduce((latest, current) => (current > latest ? current : latest), 0);
  }, [assignedTasks, profileUser, recentChanges, state?.standupNotes]);

  const completion = getUserProfileCompletion(profileUser);

  const handleFieldChange = (field: keyof EditableProfileFields, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!viewer || !canEdit) {
      return;
    }

    setSaving(true);

    try {
      await updateUser(viewer.id || viewer.email, {
        phoneNumber: form.phoneNumber.trim(),
        linkedinUrl: form.linkedinUrl.trim(),
        githubUrl: form.githubUrl.trim(),
        facebookUrl: form.facebookUrl.trim(),
      });

      if (viewer.email) {
        await reload(viewer.email);
      }

      toast.success('Profile updated');
    } catch (error) {
      console.error('[UserProfile] Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !state) {
    return <div className="p-8 text-center text-muted-foreground">Loading profile...</div>;
  }

  if (!profileUser) {
    return (
      <div className="mx-auto max-w-5xl p-6 lg:p-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">User profile not found.</p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/app/allowed-users">Back to users</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-6 lg:p-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to={userId ? '/app/allowed-users' : '/app'} className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-foreground">
            {canEdit ? 'My Profile' : `${profileUser.name}`}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Contact details, contribution metrics, and recent delivery activity.
          </p>
        </div>
        <Badge variant="outline" className="w-fit text-sm capitalize">
          {String(profileUser.role || 'member').replace(/_/g, ' ')}
        </Badge>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <Card>
          <CardHeader>
            <CardTitle>Profile Summary</CardTitle>
            <CardDescription>High-signal identity and contact details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <Avatar className="h-20 w-20 border border-border">
                <AvatarImage src={profileUser.avatar} alt={profileUser.name} />
                <AvatarFallback>{getInitials(profileUser.name, profileUser.email)}</AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-semibold text-foreground">{profileUser.name}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {profileUser.email}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {organizationName}
                  </span>
                </div>
                <UserContactLinks user={profileUser} className="mt-3" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Profile completion</div>
                <div className="mt-1 text-2xl font-semibold text-foreground">{completion.percent}%</div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className={`h-full rounded-full bg-foreground ${getProfileCompletionWidthClass(completion.percent)}`} />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Last active</div>
                <div className="mt-1 text-lg font-semibold text-foreground">{formatTimestamp(lastActiveAt)}</div>
                <div className="mt-1 text-xs text-muted-foreground">Based on task, standup, and change activity</div>
              </div>

              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Coverage gaps</div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {completion.missingFields.length === 0 ? 'Complete' : completion.missingFields.length}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {completion.missingFields.length === 0
                    ? 'Phone, LinkedIn, and GitHub are present.'
                    : completion.missingFields.map((field) => field.label).join(', ')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{canEdit ? 'Contact Details' : 'Communication Signals'}</CardTitle>
            <CardDescription>
              {canEdit
                ? 'Keep the fields below current so teammates can route work and reach you quickly.'
                : 'Direct channels and summary context for collaborating with this teammate.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {canEdit ? (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone number</Label>
                    <Input
                      id="phoneNumber"
                      value={form.phoneNumber}
                      onChange={(event) => handleFieldChange('phoneNumber', event.target.value)}
                      placeholder="+1 555 555 5555"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
                    <Input
                      id="linkedinUrl"
                      value={form.linkedinUrl}
                      onChange={(event) => handleFieldChange('linkedinUrl', event.target.value)}
                      placeholder="linkedin.com/in/your-profile"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="githubUrl">GitHub URL</Label>
                    <Input
                      id="githubUrl"
                      value={form.githubUrl}
                      onChange={(event) => handleFieldChange('githubUrl', event.target.value)}
                      placeholder="github.com/your-handle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebookUrl">Facebook URL</Label>
                    <Input
                      id="facebookUrl"
                      value={form.facebookUrl}
                      onChange={(event) => handleFieldChange('facebookUrl', event.target.value)}
                      placeholder="facebook.com/your-profile"
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
                  Missing today: {completion.missingFields.length === 0 ? 'none' : completion.missingFields.map((field) => field.label).join(', ')}.
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save profile'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-3 rounded-lg border border-border px-4 py-3">
                  <Shield className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium text-foreground">Profile readiness</div>
                    <div>{completion.percent}% complete</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border px-4 py-3">
                  <Users className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium text-foreground">Projects in scope</div>
                    <div>{relatedProjects.length} active or historical project assignments</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border px-4 py-3">
                  <Clock3 className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium text-foreground">Time logged</div>
                    <div>{totalLoggedHours.toFixed(1)} hours tracked on assigned work</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <ProfileStatCard
          label="Projects"
          value={String(relatedProjects.length)}
          helper="Direct team membership or task ownership"
          icon={FolderKanban}
        />
        <ProfileStatCard
          label="Assigned tasks"
          value={String(assignedTasks.length)}
          helper={`${activeTasks.length} currently in motion`}
          icon={Activity}
        />
        <ProfileStatCard
          label="Completed tasks"
          value={String(completedTasks.length)}
          helper={assignedTasks.length ? `${Math.round((completedTasks.length / assignedTasks.length) * 100)}% completion rate` : 'No assigned tasks yet'}
          icon={CheckCircle2}
        />
        <ProfileStatCard
          label="Hours logged"
          value={totalLoggedHours.toFixed(1)}
          helper="Summed from task time logs"
          icon={Clock3}
        />
        <ProfileStatCard
          label="Average rating"
          value={averageTaskRating ? averageTaskRating.toFixed(1) : 'N/A'}
          helper={averageTaskRating ? 'Across rated assigned tasks' : 'No task ratings yet'}
          icon={Shield}
        />
        <ProfileStatCard
          label="Task changes"
          value={String(recentChanges.length)}
          helper={recentChanges.length ? `${recentChanges.slice(0, 1)[0].changeType} is the most recent update` : 'No task change history found'}
          icon={Activity}
        />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Contribution Mix</CardTitle>
            <CardDescription>Simple counts across the collaboration surface.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MessageSquare className="h-4 w-4" /> Comments
              </div>
                <div className="mt-2 text-xl font-semibold text-foreground">{commentCount}</div>
            </div>
              <div className="rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" /> Standups
              </div>
                <div className="mt-2 text-xl font-semibold text-foreground">{standupCount}</div>
            </div>
              <div className="rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Newspaper className="h-4 w-4" /> Feed posts
              </div>
                <div className="mt-2 text-xl font-semibold text-foreground">{feedPostsCount}</div>
            </div>
              <div className="rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Activity className="h-4 w-4" /> Recent updates
              </div>
                <div className="mt-2 text-xl font-semibold text-foreground">{Math.min(recentChanges.length, 10)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Work</CardTitle>
            <CardDescription>Most recently touched tasks for this user.</CardDescription>
          </CardHeader>
          <CardContent>
            {recentTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No assigned tasks to show yet.</p>
            ) : (
              <div className="space-y-3">
                {recentTasks.map((task) => {
                  const projectName = state.projects.find((project) => project.id === task.projectId)?.name || 'Unknown project';
                  const statusBadgeClass = `border ${getStatusColors(task.status).badge}`;

                  return (
                    <Link
                      key={task.id}
                      to={`/app/projects/${task.projectId}/task/${task.id}`}
                      className="block rounded-lg border border-border px-4 py-3 transition-colors hover:border-border hover:bg-muted/40"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-foreground">{task.title}</div>
                          <div className="mt-1 text-sm text-muted-foreground">{projectName}</div>
                        </div>
                        <Badge variant="outline" className={statusBadgeClass}>{task.status}</Badge>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">Updated {formatTimestamp(task.updatedAt)}</div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Recent Change Log</CardTitle>
          <CardDescription>Latest task-level updates attributable to this user.</CardDescription>
        </CardHeader>
        <CardContent>
          {recentChanges.length === 0 ? (
            <p className="text-sm text-muted-foreground">No change-log activity recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {recentChanges.slice(0, 8).map((change) => (
                <div key={change.id} className="flex flex-col gap-2 rounded-lg border border-border px-4 py-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="font-medium text-foreground">{change.taskTitle}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {change.fieldName || change.changeType}: {change.oldValue || 'empty'} to {change.newValue || 'empty'}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{formatTimestamp(change.changedAt)}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}